import { useEffect, useRef, useState } from 'react';

import { canGoToStep, findFirstEditedStep } from './stepClearanceUtils';

export interface UseStepClearanceParams {
  activeStep: number;
  /** Skips edit detection, for while the drafts being compared are being changed by something other than the CM. */
  isEditDetectionPaused?: boolean;
  /** Called when a cleared step's data has changed since the CM advanced past it. */
  onStepEdited?: () => void;
  /**
   * Per step index, whatever the CM can edit on that step. An edit to a cleared step re-locks the
   * steps after it, since their data implicitly depends on it and their gates must run again.
   */
  stepData: Array<unknown>;
}

/**
 * Tracks how far the CM has cleared the wizard's gates so the stepper can jump around without skipping any:
 * advancing past a step with Next clears it, and an edit to it re-locks every step after it (`onStepEdited`).
 */
export function useStepClearance({
  activeStep,
  isEditDetectionPaused = false,
  onStepEdited,
  stepData,
}: UseStepClearanceParams) {
  const [clearedThrough, setClearedThrough] = useState(0);
  const clearedSignatures = useRef<Array<string | undefined>>([]);
  const previousStep = useRef(activeStep);
  const signatures = stepData.map((dataForStep) => JSON.stringify(dataForStep ?? null));
  const signaturesKey = signatures.join('\u0000');

  // Advancing by one past the furthest cleared step is what clears a gate; jumping (or Next on a
  // step that's already cleared) doesn't move the frontier.
  useEffect(() => {
    const leftStep = previousStep.current;
    previousStep.current = activeStep;

    if (activeStep === leftStep + 1 && activeStep > clearedThrough) {
      clearedSignatures.current[leftStep] = signatures[leftStep];
      setClearedThrough(activeStep);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fires on step changes only, reading that render's values
  }, [activeStep]);

  useEffect(() => {
    if (isEditDetectionPaused) {
      return;
    }

    const editedStep = findFirstEditedStep({
      clearedSignatures: clearedSignatures.current,
      clearedThrough,
      signatures,
    });

    if (editedStep !== -1) {
      setClearedThrough(editedStep);
      onStepEdited?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-checks when the data or pause state changes, not on new callbacks
  }, [signaturesKey, clearedThrough, isEditDetectionPaused]);

  function reset() {
    clearedSignatures.current = [];
    setClearedThrough(0);
  }

  return {
    canGoToStep: (step: number) => canGoToStep({ activeStep, clearedThrough, step }),
    clearedThrough,
    reset,
  };
}
